// ONE grouping implementation, for money and quantities alike.
//
// ⚠️ DO NOT reach for `toLocaleString('en-ZA', …)` in anything that can be
// PRINTED. It renders differently depending on which CLDR data the runtime
// ships, and the difference reached a grower: the first emailed statement
// printed `R 286,853.88` from Vercel's Chromium while the operator's screen
// printed `R 286 853,88` — same code, same figure, two conventions, on a
// document reconciled against the grower's own records. Node in CI agreed with
// the browser and not with the renderer, so no test could see it.
//
// Numbers on a document are not a localisation problem here: one currency, one
// audience, one convention — a business decision, not the runtime's to make.

/** Group an integer string with commas: 286853 → "286,853". */
function group(whole: string): string {
  return whole.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
}

/**
 * Fixed decimals, always padded. `286853.88` → `"286,853.88"`.
 * Use for money, where a missing cent column reads as a different number.
 */
export function formatFixed(value: number, decimals: number): string {
  const fixed = Math.abs(value).toFixed(decimals)
  const [whole, fraction] = fixed.split('.')
  // `-0.00` is arithmetically true and reads as a mistake on a document.
  const sign = value < 0 && Number(fixed) !== 0 ? '-' : ''
  return fraction ? `${sign}${group(whole)}.${fraction}` : `${sign}${group(whole)}`
}

/**
 * Up to `maxDecimals`, trailing zeros trimmed. `12500` → `"12,500"`,
 * `1.50` → `"1.5"`. Matches what `maximumFractionDigits` did, so adopting this
 * changes the separators and nothing else.
 *
 * Use for quantities — kg, bins, hectares, litres — where a trailing `.0`
 * is noise rather than precision.
 */
export function fmtQty(n: number | null | undefined, maxDecimals = 0, minDecimals = 0): string {
  if (n === null || n === undefined || !Number.isFinite(Number(n))) return '—'
  const fixed = formatFixed(Number(n), maxDecimals)
  if (maxDecimals === 0 || minDecimals >= maxDecimals) return fixed
  // Trim trailing zeros, but never below minDecimals — a stock average that
  // shows "1.5" where its column promises cents reads as a different figure.
  const [whole, fraction = ''] = fixed.split('.')
  const kept = fraction.replace(/0+$/, '').padEnd(minDecimals, '0')
  return kept ? `${whole}.${kept}` : whole
}

/** Signed, for delta columns: `+12.50` / `-3.00` / `0.00`. */
export function fmtDelta(n: number | null | undefined, decimals = 2): string {
  if (n === null || n === undefined || !Number.isFinite(Number(n))) return '—'
  const v = Number(n)
  return (v > 0 ? '+' : '') + formatFixed(v, decimals)
}
